<template>
  <TeaserAccountTile v-if="showErrorTile">
    <template v-slot:component>
      <ResponseTile
        v-if="showErrorTile"
        :back-button-label="errorButtonLabel"
        :text="errorText"
        :title="errorTitle"
        @button:back="formAbortAction()"
      ></ResponseTile>
    </template>
    <template v-slot:image>
      <TeaserTechnischerFehler />
    </template>
  </TeaserAccountTile>

  <div v-else>
    <Spinner v-if="formLoading" class="fix-spinner"></Spinner>
    <div v-else>
      <TeaserAccountTile>
        <template v-slot:component>
          <SelfServiceTile
            :changeRequestType="ChangeRequestType.DigitalCard"
            :checkPoliciesDetails="checkPoliciesDetails"
            :checkPolicyDetails="checkPolicyDetails"
            :checkPolicyOverview="checkPolicyOverview"
            :formAbortAction="formAbortAction"
            :formLoading="formLoading"
            :formShowResponse="false"
            :formSubmitDisabled="formSubmitDisabled"
            :formSubmitting="formSubmitting"
            :getFormBlocks="createFormBlocks"
            :groupPoliciesByDDSLine="groupPoliciesByDDSLine"
            :groupTitleText="'Rechtsschutz\u200Bversicherung'"
            :preSelectedPolicyIds="preSelectedPolicyIds"
            :selfServiceLabels="selfServiceLabels"
            :setSelectedPolicies="setSelectedPolicies"
            :showAttentionAlert="false"
            :showDateOption="false"
            :showInfobox="false"
            :staticPoliciesWithIconAndTitles="staticPoliciesFiltered"
            groupIcon="icon-rechtsschutz"
            @formSubmit="formSubmit"
          >
            <template #change-component>
              <div class="tc-data-row flex-column">
                <Checkbox
                  v-model="useCurrentMail"
                  isRadio
                  @click="setFormSubmitDisabled(false)"
                >
                  <template #label>{{ currentMailText }}</template>
                </Checkbox>
                <Checkbox
                  v-model="useNewMail"
                  isRadio
                  @click="setFormSubmitDisabled(false)"
                >
                  <template #label>{{ newMailText }}</template>
                </Checkbox>
              </div>
            </template>
          </SelfServiceTile>
          <br />
          <p class="self-service-selection-headline">
            {{ $t('requestDigitalCardList.footer.title') }}
          </p>
          <div>
            {{ $t('requestDigitalCardList.footer.textStart') }}
          </div>
          <br />
          <p class="self-service-selection-headline">
            {{ $t('requestDigitalCardList.footer2.title') }}
          </p>
          <div>
            {{ $t('requestDigitalCardList.footer2.text') }}
            <a
              v-if="
                t('requestDigitalCardList.footer2.text') !== '' &&
                internationalInsuranceLink !== ''
              "
              :href="sanitizeUrl(internationalInsuranceLink)"
              target="_blank"
              >hier.</a
            >
          </div>
        </template>
        <template v-slot:image>
          <TeaserWallet />
        </template>
      </TeaserAccountTile>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, type Ref, ref } from 'vue'
import { type Policy as ApiPolicy } from 'policies-api'
import {
  Checkbox,
  HintBoxColorScheme,
  HintBoxVariant,
  type KeyValueType,
  type PolicyWithIconAndTitles,
  ResponseTile,
  type SelfServiceLabels,
  SelfServiceTile,
  Spinner
} from 'de-zurich-ui-library'
import { useI18n } from 'vue-i18n'
import { getEmail, getToken } from '@/utils/KeycloakUtils'
import { navigate } from '@/utils/NavigationUtils'
import { PolicyList } from '@/utils/policy/PolicyList'
import { sanitizeUrl } from '@braintree/sanitize-url'
import { isDa, isTrue } from '@/utils/CommonUtils'
import TeaserAccountTile from '@/components/rahmen/TeaserAccountTile.vue'
import TeaserTechnischerFehler from '@/componentsTenant/rahmen/images/TeaserTechnischerFehler.vue'
import TeaserWallet from '@/componentsTenant/rahmen/images/TeaserWallet.vue'
import { usePoliciesStore } from '@/stores/policiesStore'
import { useSelfServicePolicyList } from '@/composables/useSelfServicePolicyList'
import { selfServiceListConfiguredOnMounted } from '@/utils/policy/PolicyUtils'
import { SelfServiceProcess } from '@/types/SelfServiceProcess'

const { t } = useI18n()

const policiesStore = usePoliciesStore()

const groupPoliciesByDDSLine = ['LegalProtection']

const enableDigitalCardKrad = isTrue(
  globalThis.ENV_CONFIG?.FEATURE_FLAGS?.ENABLE_DIGITAL_CARD_KRAD
)

const enableDigitalCardLegalProtection = isTrue(
  globalThis.ENV_CONFIG?.FEATURE_FLAGS?.ENABLE_DIGITAL_CARD_LEGAL_PROTECTION
)

const {
  staticPoliciesFiltered,
  formLoading,
  showErrorTile,
  loadPolicies,
  setFormLoading,
  setShowErrorTile
} = useSelfServicePolicyList(() =>
  selfServiceListConfiguredOnMounted({
    includeKrad: enableDigitalCardKrad,
    includeDDSLines: enableDigitalCardLegalProtection ? ['LegalProtection'] : []
  })
)

// authorization and redirects
const internationalInsuranceLink: string =
  globalThis.ENV_CONFIG?.REDIRECTS.INTERNATIONAL_INSURANCE || ''
const selectedPolicies: Ref<PolicyWithIconAndTitles[]> = ref([])
const isMultiSelection = ref(false)
const selectedMail = ref<boolean | null>(null)
const errorTitle = ref('')
const errorText = ref('')
const errorButtonLabel = ref('')
const formSubmitDisabled = ref(true)
const selfServiceLabels = computed<SelfServiceLabels>(() => ({
  errorText: t('requestDigitalCardList.error.errorTextTech').toString(),
  errorTitle: t('requestDigitalCardList.error.errorTitle').toString(),
  formCardTitle: t('requestDigitalCardList.form.formCardTitle').toString(),
  formCurrentDataLabel: '',
  formCurrentDataValue: '',
  formDateErrorText: '',
  formDateLabel: '',
  formSubmitButtonLabel: t(
    !formSubmitDisabled.value && selectedMail.value === false
      ? 'requestDigitalCardList.form.formChangeEmailButtonLabel'
      : 'requestDigitalCardList.form.formSubmitButtonLabel'
  ).toString(),
  listHeadline: t('requestDigitalCardList.list.listHeadline').toString(),
  responseText: '',
  responseTileBackButtonLabel: t('requestDigitalCardList.error.buttonLabel').toString(),
  responseTitle: ''
}))
const blackListedPolicyIDs: Set<string> = new Set<string>()
const preSelectedPolicyIds: Ref<string[]> = ref([])

const formSubmitting = ref(false)
const useCurrentMail = computed<boolean>({
  get: () => selectedMail.value === true,
  set: (value) => {
    selectedMail.value = value ? true : null
  }
})
const useNewMail = computed<boolean>({
  get: () => selectedMail.value === false,
  set: (value) => {
    selectedMail.value = value ? false : null
  }
})
const currentMailText = ref(getEmail() ?? '')
const newMailText = ref(
  'Falls Sie die E-Mail Adresse ändern möchten, wählen sie diese Option.'
)

enum ChangeRequestType {
  DigitalCard = 'DigitalCard'
}

onMounted(async () => {
  // Preselection: consume all policy ids once for the self-service list
  preSelectedPolicyIds.value = policiesStore.consumePreSelectedPolicyIds()

  const filtered = await loadPolicies()
  applyGroupHint(filtered)
  if (filtered.length === 0) {
    setErrorTileNoContracts()
  }
  setFormLoading(false)
})

/**
 * Setzt die Hint-Box-Inhalte (Titel/Text/Farbe) auf der ersten Police der gruppierten
 * DDS-Linie(n), damit SelfServiceTile diese als Gruppen-Hinweis übernimmt
 * (siehe buildGroupedPoliciesBase, das die Hint-Felder von groupedPolicies[0] liest).
 * Die Texte werden mandantenspezifisch aus den Locales (RequestDigitalCardList.locale.json)
 * bezogen, damit sie je Mandant (da/zur) individuell konfiguriert werden können.
 *
 * Hinweis: loadPolicies() liefert immer PolicyWithIconAndTitles[] (UI-Library-Typ mit
 * Hint-Feldern), nicht den rohen API-Typ Policy aus 'policies-api' (der keine Hint-Felder
 * besitzt) – daher hier bewusst kein Union-Typ mit dem API-Policy.
 */
function applyGroupHint(policies: PolicyWithIconAndTitles[]): void {
  const groupedDdsLinesSet = new Set(groupPoliciesByDDSLine)
  const firstGroupedPolicy = policies.find((policy) =>
    policy.ddsLines?.some((ddsLine) => groupedDdsLinesSet.has(ddsLine))
  )
  if (!firstGroupedPolicy) return

  firstGroupedPolicy.hintTitle = t('requestDigitalCardList.groupInfo.hintTitle')
  firstGroupedPolicy.hintContent = t('requestDigitalCardList.groupInfo.hintContent')
  firstGroupedPolicy.hintColorScheme = t(
    'requestDigitalCardList.groupInfo.hintColorScheme'
  ) as HintBoxColorScheme
  firstGroupedPolicy.hintVariant = HintBoxVariant.STANDALONE
  firstGroupedPolicy.hintExtended = true
}

function setErrorTileNoContracts() {
  errorTitle.value = t('requestDigitalCardList.error.errorTitle')
  errorText.value = t('requestDigitalCardList.error.errorTextNoContracts')
  errorButtonLabel.value = t('requestDigitalCardList.error.buttonLabel')
  setShowErrorTile(true)
}

function setErrorTileServiceDown() {
  errorTitle.value = t('requestDigitalCardList.response.serviceErrorTitle')
  errorText.value = t('requestDigitalCardList.response.serviceErrorText')
  errorButtonLabel.value = t('requestDigitalCardList.error.buttonLabel')
  setShowErrorTile(true)
}

function checkPolicyDetails(policy: ApiPolicy): boolean {
  return PolicyList.checkPolicyDetails(policy)
}

function checkPolicyOverview(policy: ApiPolicy): boolean {
  return PolicyList.checkPolicyOverview(policy, blackListedPolicyIDs)
}

function setFormSubmitDisabled(value: boolean): void {
  formSubmitDisabled.value = value
}

async function formSubmit(): Promise<void> {
  if (selectedMail.value === false) {
    policiesStore.setProcessReturnContext(
      SelfServiceProcess.REQUEST_DIGITALCARD_PAGE,
      selectedPolicies.value.map((policy) => policy.policyId)
    )
    navigate(globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_EMAIL_PAGE)
    return
  }

  await submitDigitalCardRequest()
}

async function submitDigitalCardRequest(): Promise<void> {
  formSubmitting.value = true
  formLoading.value = true

  try {
    const token = await getToken()
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/contracts/digital-pass`,
      {
        method: 'POST',
        body: JSON.stringify(selectedPolicies.value.map((policy) => policy.policyId)),
        headers: {
          Authorization: `Bearer ${token}`,
          KeyId: globalThis.ENV_CONFIG?.API_KEY,
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      setErrorTileServiceDown()
      return
    }

    policiesStore.setDigitalCardThankYouValues({
      mail: getEmail() ?? '',
      isMulti: isMultiSelection.value
    })
    navigate(
      `/danke?${isDa() ? 'digitale-schadenkarte-anfordern' : 'digitale-versicherungskarte-anfordern'}`
    )
  } catch (error) {
    console.error('DigitalCard-Anfrage fehlgeschlagen.', {
      error: error instanceof Error ? error.message : String(error)
    })
    setErrorTileServiceDown()
  } finally {
    formLoading.value = false
    formSubmitting.value = false
  }
}

function setSelectedPolicies(
  policies: PolicyWithIconAndTitles[],
  isMulti: boolean
): void {
  selectedPolicies.value = policies
  isMultiSelection.value = isMulti
}

function formAbortAction(): void {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT)
}

function checkPoliciesDetails(policies: ApiPolicy[]): boolean {
  return PolicyList.checkPoliciesDetails(policies, blackListedPolicyIDs)
}

function createFormBlocks(policies: ApiPolicy[]): KeyValueType[][] {
  return PolicyList.getFormBlocks(policies, 'requestDigitalCardList')
}
</script>

<style lang="scss" scoped>
.fix-spinner {
  position: relative !important;
  top: 64px !important;
}

.is-styled {
  .self-service-selection-headline {
    font-family: var(--sst-headline-font-family), serif;
    font-size: 20px;
    color: var(--sst-headline-color) !important;
    margin-top: 0;
    margin-bottom: calc(var(--baseline) * 2) !important;
  }
}
</style>
